import { useCallback } from 'react';

import { Button } from '@/lib/base/buttons/button';
import type { TimeFormat } from '@/lib/time';
import { timeParts, useOrgTimeFormat } from '@/lib/time';
import type { CalendarEvent } from '@/pages/calendar/utils/eventMapper';

function formatMeetingTime(
  startIso: string,
  endIso: string,
  format: TimeFormat,
  timeZone?: string
): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      ...timeParts(format),
      ...(timeZone ? { timeZone } : {}),
    });
    return `${fmt.format(start)} – ${fmt.format(end)}`;
  } catch {
    if (!timeZone) {
      return '';
    }
    // Invalid timezone — fall back to browser local time
    try {
      const fmt = new Intl.DateTimeFormat('en-US', timeParts(format));
      return `${fmt.format(start)} – ${fmt.format(end)}`;
    } catch {
      return '';
    }
  }
}

export interface MeetingsNeedingNotesListProps {
  meetings: CalendarEvent[];
  /**
   * Opens the note composer for a meeting. Undefined when the user lacks the
   * ahoy_note create grant — the "Add summary" button is then hidden.
   */
  onAddNote?: (meetingEntityId: string) => void;
  timeZone?: string;
  /** Optional heading text. Default: 'Meetings needing notes'. */
  heading?: string;
}

interface MeetingNeedsNoteRowProps {
  meeting: CalendarEvent;
  onAddNote?: (meetingEntityId: string) => void;
  format: TimeFormat;
  timeZone?: string;
}

function MeetingNeedsNoteRow({
  meeting,
  onAddNote,
  format,
  timeZone,
}: MeetingNeedsNoteRowProps) {
  const handleClick = useCallback(
    () => onAddNote?.(meeting.id),
    [meeting.id, onAddNote]
  );
  return (
    <div className='flex items-center justify-between gap-3 px-3 py-2 rounded-[4px] border border-secondary bg-white dark:bg-neutral-900'>
      <div className='min-w-0 flex-1'>
        <p className='text-[13px] font-medium text-[var(--color-text-primary)] truncate'>
          {meeting.title}
        </p>
        <p className='text-[12px] text-[var(--color-text-secondary)]'>
          {formatMeetingTime(
            meeting.start_time,
            meeting.end_time,
            format,
            timeZone
          )}
        </p>
      </div>
      {onAddNote && (
        <Button size='xs' color='secondary' onClick={handleClick}>
          Add summary
        </Button>
      )}
    </div>
  );
}

export function MeetingsNeedingNotesList({
  meetings,
  onAddNote,
  timeZone,
  heading = 'Meetings needing summary',
}: MeetingsNeedingNotesListProps) {
  const { timeFormat } = useOrgTimeFormat();
  if (meetings.length === 0) {
    return null;
  }
  return (
    <div className='space-y-2'>
      <h3 className='text-[13px] font-medium text-[var(--color-text-secondary)]'>
        {heading}
      </h3>
      <div className='space-y-1.5'>
        {meetings.map(m => (
          <MeetingNeedsNoteRow
            key={m.id}
            meeting={m}
            onAddNote={onAddNote}
            format={timeFormat}
            timeZone={timeZone}
          />
        ))}
      </div>
    </div>
  );
}
